import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearchQuery } from '../slices/searchApiSlice';
import { useSearchContext } from '../providers/SearchProvider';
import { useBreakpoints } from '../hooks/useBreakpoints';
import { getDashboardHomeMobileScrollEl, scrollDashboardHomeToTop } from '../utils/dashboardHomeMobileScroll';
import FalconCloseButton from './common/FalconCloseButton';
import NavbarSeasonIllustration from './common/NavbarSeasonIllustration';

const GLOBAL_SEARCH_INPUT_ID = 'globalSearchKeyword';
const MIN_QUERY_LENGTH = 2;

const GlobalSearchBox = ({
	autoFocus = false,
	placeholder: placeholderProp,
	onDismiss,
	variant = 'default',
	onFocusStateChange,
	showSeasonalIllustration = false,
}) => {
	const { breakpoints } = useBreakpoints();
	const isIslandVariant = variant === 'island';
	/** 底部岛搜索：结果在首页展示，不跳转 */
	const stayOnCurrentPage = isIslandVariant;
	const isMobileCompact = breakpoints.down('md');
	const showDismissControl = Boolean(onDismiss) || isIslandVariant;
	const defaultPlaceholder = breakpoints.down('md') ? '搜索...' : '搜索订单、客户、产品...';
	const searchPlaceholder = placeholderProp ?? defaultPlaceholder;
	const [keyword, setKeyword] = useState('');
	const [debouncedKeyword, setDebouncedKeyword] = useState('');
	const [submittedKeyword, setSubmittedKeyword] = useState('');
	const navigate = useNavigate();
	const { setShowSearchCardState, setSearchResultsState, clearSearchResults, setSearchingState, showSearchCard, searchKeyword } = useSearchContext();
	const inputRef = useRef(null);
	const debounceTimerRef = useRef(null);
	const isFocusedRef = useRef(false);
	const isUserTypingRef = useRef(false);
	const lastUserInputRef = useRef('');

	const activeQuery = isIslandVariant ? submittedKeyword : debouncedKeyword;

	// currentData 只属于当前关键词；data 会保留上一次查询的结果，换词时不能拿来展示
	const {
		currentData: searchResults,
		isFetching,
		isLoading,
		isError,
	} = useGlobalSearchQuery(
		{ q: activeQuery, limit: 12 }, // 与 SearchResultsCard GLOBAL_SEARCH_RESULT_LIMIT 对齐
		{ skip: !activeQuery || activeQuery.length < MIN_QUERY_LENGTH },
	);

	// 未聚焦时才把全局关键词写回输入框。聚焦中（含刚清空）不回填，避免旧词覆盖正在输入的新词。
	useEffect(() => {
		if (isFocusedRef.current || isUserTypingRef.current) {
			return;
		}

		if (searchKeyword && searchKeyword !== keyword) {
			setKeyword(searchKeyword);
			if (isIslandVariant) {
				setSubmittedKeyword(searchKeyword);
			} else {
				setDebouncedKeyword(searchKeyword);
			}
		} else if (!searchKeyword && !showSearchCard && keyword) {
			setKeyword('');
			setDebouncedKeyword('');
			setSubmittedKeyword('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchKeyword, showSearchCard, isIslandVariant]);

	// 桌面/顶栏：防抖更新查询词
	useEffect(() => {
		if (isIslandVariant) {
			return undefined;
		}

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		if (!keyword.trim()) {
			setDebouncedKeyword('');
			return undefined;
		}

		const debounceTime = keyword.length <= MIN_QUERY_LENGTH ? 500 : 300;

		debounceTimerRef.current = setTimeout(() => {
			setDebouncedKeyword(keyword.trim());
		}, debounceTime);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [keyword, isIslandVariant]);

	// 查询结果写回全局状态。只提交当前关键词自己的结果，避免清空后再搜时带上一次的命中。
	useEffect(() => {
		if (!isIslandVariant && !keyword.trim()) {
			if (!isFocusedRef.current) {
				return;
			}
			const isModalOpen = document.querySelector('.modal.show') !== null;
			if (!isModalOpen) {
				setSearchResultsState(null, '');
				navigate('/dashboard');
				setShowSearchCardState(true);
			}
			return;
		}

		if (activeQuery && activeQuery.length >= MIN_QUERY_LENGTH) {
			if (!stayOnCurrentPage) {
				navigate('/dashboard');
			}
			setShowSearchCardState(true);

			if (searchResults) {
				setSearchResultsState(searchResults, activeQuery);
				return;
			}

			if (isFetching || isLoading) {
				setSearchResultsState(null, activeQuery);
				setSearchingState(true);
				return;
			}

			if (isError) {
				setSearchResultsState(null, activeQuery);
			}
			return;
		}

		if (isIslandVariant) {
			return;
		}
	}, [
		activeQuery,
		isError,
		isFetching,
		isIslandVariant,
		isLoading,
		keyword,
		navigate,
		searchResults,
		setSearchResultsState,
		setSearchingState,
		setShowSearchCardState,
		stayOnCurrentPage,
	]);

	const dismissSearchFocus = useCallback(
		({ clearAll = false } = {}) => {
			if (clearAll) {
				setKeyword('');
				setSubmittedKeyword('');
				clearSearchResults();
			} else if (!isIslandVariant && !keyword.trim()) {
				setShowSearchCardState(false);
			}
			isFocusedRef.current = false;
			isUserTypingRef.current = false;
			inputRef.current?.blur();
			if (isIslandVariant) {
				onFocusStateChange?.(false);
				if (clearAll) {
					onDismiss?.();
				}
				return;
			}
			onDismiss?.();
		},
		[keyword, clearSearchResults, isIslandVariant, onDismiss, onFocusStateChange, setShowSearchCardState],
	);

	useEffect(() => {
		if (!showDismissControl || isIslandVariant) return undefined;

		const shouldIgnoreTarget = (target) => {
			if (!(target instanceof Element)) return false;
			return Boolean(inputRef.current?.closest('.search-box')?.contains(target));
		};

		const handlePointerDown = (event) => {
			if (document.activeElement !== inputRef.current) return;
			if (shouldIgnoreTarget(event.target)) return;
			dismissSearchFocus();
		};

		const handleTouchMove = () => {
			if (document.activeElement === inputRef.current) {
				dismissSearchFocus();
			}
		};

		document.addEventListener('pointerdown', handlePointerDown, true);
		window.addEventListener('touchmove', handleTouchMove, { passive: true });
		return () => {
			document.removeEventListener('pointerdown', handlePointerDown, true);
			window.removeEventListener('touchmove', handleTouchMove);
		};
	}, [dismissSearchFocus, isIslandVariant, showDismissControl]);

	const handleInputChange = useCallback(
		(e) => {
			const value = e.target.value;
			isUserTypingRef.current = true;
			lastUserInputRef.current = value;
			setKeyword(value);

			if (isIslandVariant) {
				window.setTimeout(() => {
					if (lastUserInputRef.current === value) {
						isUserTypingRef.current = false;
					}
				}, 200);
				return;
			}

			if (!value.trim()) {
				setDebouncedKeyword('');
				setSubmittedKeyword('');
				setSearchResultsState(null, '');
				if (!stayOnCurrentPage) {
					navigate('/dashboard');
				}
				setShowSearchCardState(true);
			}

			window.setTimeout(() => {
				if (lastUserInputRef.current === value) {
					isUserTypingRef.current = false;
				}
			}, 600);
		},
		[isIslandVariant, navigate, setSearchResultsState, setShowSearchCardState, stayOnCurrentPage],
	);

	const handleInputFocus = useCallback(
		(event) => {
			isFocusedRef.current = true;
			isUserTypingRef.current = false;

			if (isIslandVariant) {
				const scrollEl = getDashboardHomeMobileScrollEl();
				const scrollTop = scrollEl?.scrollTop ?? 0;
				setShowSearchCardState(true);
				onFocusStateChange?.(true);
				requestAnimationFrame(() => {
					if (scrollEl != null) {
						scrollEl.scrollTop = scrollTop;
					}
					event.target?.focus?.({ preventScroll: true });
				});
				return;
			}

			if (!stayOnCurrentPage) {
				navigate('/dashboard');
			}
			setShowSearchCardState(true);
			onFocusStateChange?.(true);
		},
		[isIslandVariant, navigate, onFocusStateChange, setShowSearchCardState, stayOnCurrentPage],
	);

	const handleInputBlur = useCallback(() => {
		window.setTimeout(() => {
			if (document.activeElement !== inputRef.current) {
				isFocusedRef.current = false;
				window.setTimeout(() => {
					isUserTypingRef.current = false;
				}, 100);
				if (isIslandVariant) {
					onFocusStateChange?.(false);
					return;
				}
				if (showDismissControl) {
					if (!keyword.trim()) {
						setShowSearchCardState(false);
					}
					onDismiss?.();
				}
			}
		}, 200);
	}, [isIslandVariant, keyword, onDismiss, onFocusStateChange, setShowSearchCardState, showDismissControl]);

	const handleKeyDown = useCallback(
		(e) => {
			const isModalOpen = document.querySelector('.modal.show') !== null;
			if (e.key === 'Escape' && !isModalOpen) {
				e.preventDefault();
				e.stopPropagation();
				dismissSearchFocus({ clearAll: true });
			}
		},
		[dismissSearchFocus],
	);

	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault();
			if (!isIslandVariant) {
				return;
			}

			const trimmed = keyword.trim();
			if (!trimmed) {
				setSubmittedKeyword('');
				clearSearchResults();
				inputRef.current?.blur();
				return;
			}

			if (trimmed.length < MIN_QUERY_LENGTH) {
				return;
			}

			if (trimmed === submittedKeyword && searchKeyword === trimmed) {
				inputRef.current?.blur();
				return;
			}

			setSubmittedKeyword(trimmed);
			setShowSearchCardState(true);
			scrollDashboardHomeToTop({ smooth: false });
			inputRef.current?.blur();
		},
		[clearSearchResults, isIslandVariant, keyword, searchKeyword, setShowSearchCardState, submittedKeyword],
	);

	const handleClose = useCallback(() => {
		dismissSearchFocus({ clearAll: true });
	}, [dismissSearchFocus]);

	useEffect(() => {
		if (isMobileCompact) {
			return undefined;
		}

		const handleGlobalKeyDown = (e) => {
			const activeElement = document.activeElement;
			const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA' || activeElement?.isContentEditable;

			const isModalOpen = document.querySelector('.modal.show') !== null;
			if (e.key === 'Escape' && inputRef.current === activeElement) {
				if (isModalOpen) {
					return;
				}
				e.preventDefault();
				e.stopPropagation();
				dismissSearchFocus({ clearAll: true });
				return;
			}

			const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
			const isModifierPressed = isMac ? e.metaKey : e.ctrlKey;
			const isKKey = e.key === 'k' || e.key === 'K';

			if (isModifierPressed && isKKey) {
				if (!isInputFocused) {
					e.preventDefault();
					e.stopPropagation();

					if (inputRef.current) {
						inputRef.current.focus();
						isFocusedRef.current = true;
						navigate('/dashboard');
						setShowSearchCardState(true);
					}
				}
			}
		};

		window.addEventListener('keydown', handleGlobalKeyDown);
		return () => window.removeEventListener('keydown', handleGlobalKeyDown);
	}, [dismissSearchFocus, isMobileCompact, navigate, setShowSearchCardState]);

	useEffect(() => {
		if (!autoFocus) return undefined;
		const timer = window.setTimeout(() => {
			inputRef.current?.focus({ preventScroll: true });
		}, 0);
		return () => window.clearTimeout(timer);
	}, [autoFocus]);

	return (
		<Form
			className={`position-relative search-box${isIslandVariant ? ' search-box--island workbench-island-search-field' : ''}${
				showSeasonalIllustration ? ' search-box--with-season' : ''
			}`}
			onSubmit={handleSubmit}
		>
			<Form.Label htmlFor={GLOBAL_SEARCH_INPUT_ID} className='visually-hidden'>
				{searchPlaceholder}
			</Form.Label>
			<Form.Control
				ref={inputRef}
				id={GLOBAL_SEARCH_INPUT_ID}
				name={GLOBAL_SEARCH_INPUT_ID}
				type='search'
				inputMode={isIslandVariant ? 'search' : undefined}
				enterKeyHint={isIslandVariant ? 'search' : undefined}
				placeholder={searchPlaceholder}
				aria-label={searchPlaceholder}
				className='rounded-pill search-input'
				value={keyword}
				onChange={handleInputChange}
				onFocus={handleInputFocus}
				onBlur={handleInputBlur}
				onKeyDown={handleKeyDown}
				autoComplete='off'
			/>
			<FontAwesomeIcon icon='search' className={`position-absolute search-box-icon${isIslandVariant ? '' : ' text-400'}`} />
			{showSeasonalIllustration ? <NavbarSeasonIllustration /> : null}
			{showDismissControl && (
				<div className='search-box-close-btn-container'>
					<FalconCloseButton size='sm' noOutline className='fs-11' onClick={handleClose} aria-label='关闭搜索' />
				</div>
			)}
		</Form>
	);
};

GlobalSearchBox.propTypes = {
	autoFocus: PropTypes.bool,
	placeholder: PropTypes.string,
	onDismiss: PropTypes.func,
	variant: PropTypes.oneOf(['default', 'island']),
	onFocusStateChange: PropTypes.func,
	showSeasonalIllustration: PropTypes.bool,
};

export default GlobalSearchBox;
